import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';
import {
  INVOICE_REMINDER_RUN_STARTED,
  invoiceRemindersText,
} from './invoiceRemindersText';

export const RUN_POLL_MS = 2000;
// The statuses of api invoice_reminder_runs that end a run: any other is waited on.
export const FINISHED_RUN_STATUSES = ['done', 'failed', 'skipped'];
// Reads that fail in a row before the run is let go (a minute at 2 s).
export const MAX_FAILED_RUN_READS = 30;

const ReminderRunReport = ({ lines }) => (
  <div className="flex flex-col gap-1">
    {lines.map((line, index) => (
      <div key={index} className={index === 0 ? 'font-medium' : 'text-sm'}>
        {line}
      </div>
    ))}
  </div>
);

const reportRun = (text, run) => {
  const counters = run.counters ?? {};
  const lines = [
    text(`run.${run.status}`),
    text('run.counters', {
      sent: counters.sent ?? 0,
      failed: counters.failed ?? 0,
      noRecipient: counters.noRecipient ?? 0,
      settled: counters.settled ?? 0,
      alreadyClaimed: counters.alreadyClaimed ?? 0,
    }),
  ];
  const report = <ReminderRunReport lines={lines} />;
  if (run.status === 'failed') toast.error(report);
  else if (run.status === 'skipped') toast.info(report);
  else toast.success(report);
};

/**
 * Follows a "Tout relancer" run (JBWEB-1283): the confirmation sends
 * INVOICE_REMINDER_RUN_STARTED with its `runId`, read again every two seconds
 * until it is done, failed or skipped. Then the counters go in a toast and
 * `onFinished` reads the list and the summary again. Held by the page, so that
 * the run is still followed from another tab.
 *
 * Nobody resets the return, so the one already there when the page mounts is
 * an old run, maybe reported long ago: it is skipped, as in
 * useInvoiceReminderChanged. The cost: a page mounted again mid-run no longer
 * follows that run.
 */
const useReminderRunTracking = (onFinished) => {
  const { modalDataReturn, language } = UseContext();
  const [runId, setRunId] = useState(null);
  const lastSeenReturn = useRef(modalDataReturn);
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;
  const languageRef = useRef(language);
  languageRef.current = language;

  useEffect(() => {
    if (lastSeenReturn.current === modalDataReturn) return;
    lastSeenReturn.current = modalDataReturn;
    if (
      modalDataReturn?.type === INVOICE_REMINDER_RUN_STARTED &&
      modalDataReturn.runId
    ) {
      setRunId(modalDataReturn.runId);
    }
  }, [modalDataReturn]);

  useEffect(() => {
    if (!runId) return undefined;
    let active = true;
    let timer = null;
    let failedReads = 0;
    const text = (path, values) =>
      invoiceRemindersText(languageRef.current)(path, values);

    const finish = () => {
      setRunId(null);
      onFinishedRef.current?.();
    };

    const poll = () => {
      ApiDatabase.getInvoiceReminderRun(
        { runId },
        (data) => {
          if (!active) return;
          failedReads = 0;
          const run = data?.run;
          if (run && FINISHED_RUN_STATUSES.includes(run.status)) {
            reportRun(text, run);
            finish();
            return;
          }
          timer = setTimeout(poll, RUN_POLL_MS);
        },
        (error) => {
          if (!active || error?.name === 'CanceledError') return;
          failedReads += 1;
          // A run that no longer exists is not waited for; a failed read is retried for a while.
          if (
            error?.response?.status === 404 ||
            failedReads >= MAX_FAILED_RUN_READS
          ) {
            toast.error(text('run.lost'));
            finish();
            return;
          }
          timer = setTimeout(poll, RUN_POLL_MS);
        },
      );
    };
    poll();

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [runId]);

  return { runId, isRunning: Boolean(runId) };
};

export default useReminderRunTracking;
